import { useTranslation } from 'react-i18next';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { Eye, Plus, Send, ThreeDotsVertical, Trash } from 'react-bootstrap-icons';

import { Empty, Popover, Row, Table } from 'antd';

import { useCallback, useEffect, useState } from 'react';

import { useNotificationContext } from '../../../contexts/NotificationContext';

import CustomNewsletterGeneration from './CustomNewsletterGeneration';
import { ActionMenu } from '../../../components/common/ActionMenu';
import { getNewsLetters } from '../../home/components/newsLetter/getNewsLetters';
import {
  deleteNewsletter,
  publishNewsletter,
  unpublishNewsletter,
} from '../../../api/services/newsLetterService';
import { useNavigate } from 'react-router-dom';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import { usePermissions } from '../../../hooks/usePermissions';

const NewsletterManagement = () => {
  const { t } = useTranslation('newsletter');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManageNewsletters = hasPermission(PermissionEnum.MANAGE_NEWSLETTERS);

  const [showCustomNewsletterGeneration, setShowCustomNewsletterGeneration] =
    useState<boolean>(false);

  const [savedNewsletterData, setSavedNewsletterData] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingNewsletter, setIsLoadingNewsletter] = useState<boolean>(false);
  // const [pageSize] = useState<number>(8);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalNewsletters, setTotalNewsletters] = useState<number>(0);
  const { successNotification, errorNotification } = useNotificationContext();
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [selectedNewsletterId, setSelectedNewsletterId] = useState<number>();

  const { isOnline } = useIsOnlineContext();

  const navigateToViewNewsLetter = (id: number) => {
    navigate(`newsletter/view/${id}`, { state: 'administration' });
  };

  const handleAddNewsletter = () => {
    if (!canManageNewsletters) return;

    setShowCustomNewsletterGeneration(true);
  };

  const handleUpdateNewBtn = () => {
    setShowCustomNewsletterGeneration(false);
    getSavedNewsletters();
  };

  const handleDeleteNewsletter = async () => {
    if (!canManageNewsletters) return;

    try {
      const result = selectedNewsletterId && (await deleteNewsletter(selectedNewsletterId));

      setIsDeleteConfirmationOpen(false);
      successNotification(t('newsletterDeleteSuccess'));
      getSavedNewsletters();
    } catch (error) {
      console.error(error);
      setIsDeleteConfirmationOpen(false);
      errorNotification(t('newsletterDeleteFail'));
    }
  };

  const handlePublishNewsletter = async (recordId: any, published: boolean) => {
    if (!canManageNewsletters) return;

    if (published) {
      // unpublish
      try {
        const result = await unpublishNewsletter(recordId);

        successNotification(t('newsletterUnpublishSuccess'));
        getSavedNewsletters();
      } catch (error) {
        console.error(error);
        errorNotification(t('newsletterUnpublishFail'));
      }
    } else {
      // publish
      try {
        const result = await publishNewsletter(recordId);

        successNotification(t('newsletterPublishSuccess'));
        getSavedNewsletters();
      } catch (error) {
        console.error(error);
        errorNotification(t('newsletterPublishFail'));
      }
    }
  };

  const columns = [
    {
      title: t('title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: t('summary'),
      dataIndex: 'summary',
      key: 'summary',
      width: 250,
      ellipsis: true,
    },
    {
      title: t('createdAt'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      render: (date: number) => new Date(Number(date)).toLocaleDateString(),
    },
    {
      title: t('action'),
      dataIndex: 'action',
      key: 'action',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, record: any) => {
        const menuItems = [
          {
            text: t('view'),
            icon: <Eye size={16} className="text-primary-blue-icon" />,
            click: () => navigateToViewNewsLetter(record?.id),
          },
          {
            text: record?.published ? t('unpublish') : t('publish'),
            icon: <Send size={16} className="text-primary-blue-icon" />,
            click: () => handlePublishNewsletter(record?.id, record?.published),
          },
          {
            text: t('delete'),
            icon: <Trash size={16} className="text-primary-blue-icon" />,
            click: () => getDeleteConfirmation(record?.id),
          },
        ];

        return (
          <Popover
            content={<ActionMenu menuItems={menuItems} />}
            className="cursor-pointer"
            placement="bottom"
            trigger="hover"
            overlayStyle={{ zIndex: 800 }}
          >
            <Row justify="center">
              <ThreeDotsVertical size={15} />
            </Row>
          </Popover>
        );
      },
    },
  ];

  const getSavedNewsletters = useCallback(
    async (page: number = currentPage, size: number = pageSize) => {
      setIsLoadingNewsletter(true);
      try {
        const response = await getNewsLetters(page, size, undefined);

        if (response.statusText === 'SUCCESS') {
          const newsletters = response.data?.data || response.data || [];
          const total = response.data?.total || 0;
          setSavedNewsletterData(newsletters);
          setTotalNewsletters(total);
          setCurrentPage(page);
        } else {
          console.error(t('newslettersFetchError'), response);
        }
      } catch (error) {
        console.error(t('newslettersFetchError'), error);
      } finally {
        setIsLoadingNewsletter(false);
      }
    },
    [pageSize, currentPage, errorNotification, t]
  );

  useEffect(() => {
    getSavedNewsletters();
  }, []);

  /* const handlePageChange = (page: number) => {
    getSavedNewsletters(page);
  }; */

  const cancelDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const getDeleteConfirmation = (id: number) => {
    if (!canManageNewsletters) return;

    setIsDeleteConfirmationOpen(true);
    setSelectedNewsletterId(id);
  };

  if (!canManageNewsletters) {
    return null;
  }

  return (
    <>
      <ConfirmModelComponent
        isOpen={isDeleteConfirmationOpen}
        onClickedCancel={cancelDeleteConfirmation}
        header={t('confirmDeleteTitle')}
        content={<p>{t('confirmDeleteMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleDeleteNewsletter,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelDeleteConfirmation,
          },
        }}
        styleClasses="z-100 p-6"
      />

      {!showCustomNewsletterGeneration && (
        <div className="mt-6 w-full">
          <div className="mb-6 flex justify-end">
            <PrimarySquareBtn handleOnClick={handleAddNewsletter} disabled={!isOnline}>
              <div className="flex">
                <Plus className="h-6 w-6 text-white" />
                <p className="pt-1">{t('newNewsletter')}</p>
              </div>
            </PrimarySquareBtn>
          </div>

          {/* {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4 text-red-700">
          Error loading news articles. Please try again.
        </div>
      )} */}

          <div className="mb-6 w-full overflow-x-auto">
            <Table
              dataSource={savedNewsletterData}
              columns={columns}
              rowKey="id"
              loading={isLoadingNewsletter}
              size="middle"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total}`,
                total: totalNewsletters,
                // onChange: handlePageChange,
                onChange: (page: number, size?: number) => {
                  setCurrentPage(page);
                  if (size && size !== pageSize) {
                    setPageSize(size);
                  }
                  getSavedNewsletters(page, size);
                },
                position: ['bottomLeft'],
                locale: {
                  jump_to: t('goTo'),
                  page: t('page'),
                  items_per_page: `/ ${t('page')}`,
                },
              }}
              scroll={{ x: 800 }}
              locale={{
                emptyText: (
                  <Empty
                    className="bg-gray-bg-color py-5"
                    description={savedNewsletterData?.length === 0 ? t('noDataRecords') : null}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </div>
        </div>
      )}

      {showCustomNewsletterGeneration && (
        <CustomNewsletterGeneration handleUpdateNewBtn={handleUpdateNewBtn} />
      )}
    </>
  );
};

export default NewsletterManagement;
